/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Journeys", description = "Journeys give marketers the tools to automate campaigns.", lastUpdatedDate = new Date(1743552000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Journeys",
        description: "Journeys give marketers the tools to automate campaigns.",
        lastUpdatedDate: "Wed Apr 02 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Required setup",
          "depth": 0,
          "id": "required-setup"
        }, {
          "value": "Build a journey",
          "depth": 0,
          "id": "build-a-journey"
        }, {
          "value": "Create new journey",
          "depth": 1,
          "id": "create-new-journey"
        }, {
          "value": "Edit view",
          "depth": 1,
          "id": "edit-view"
        }, {
          "value": "Interacting with the canvas",
          "depth": 1,
          "id": "interacting-with-the-canvas"
        }, {
          "value": "Connecting tiles",
          "depth": 1,
          "id": "connecting-tiles"
        }, {
          "value": "Start tile",
          "depth": 1,
          "id": "start-tile"
        }, {
          "value": "Evaluation schedule",
          "depth": 1,
          "id": "evaluation-schedule"
        }, {
          "value": "Starting a journey",
          "depth": 1,
          "id": "starting-a-journey"
        }, {
          "value": "Flexibility",
          "depth": 1,
          "id": "flexibility"
        }, {
          "value": "Entry and Exit criteria",
          "depth": 0,
          "id": "entry-and-exit-criteria"
        }, {
          "value": "Audience-based entry",
          "depth": 1,
          "id": "audience-based-entry"
        }, {
          "value": "Event-based entry",
          "depth": 1,
          "id": "event-based-entry"
        }, {
          "value": "Exit criteria",
          "depth": 1,
          "id": "exit-criteria"
        }, {
          "value": "Tile types",
          "depth": 0,
          "id": "tile-types"
        }, {
          "value": "Send to destination",
          "depth": 1,
          "id": "send-to-destination"
        }, {
          "value": "Time delay",
          "depth": 1,
          "id": "time-delay"
        }, {
          "value": "Hold until",
          "depth": 1,
          "id": "hold-until"
        }, {
          "value": "Segment",
          "depth": 1,
          "id": "segment"
        }, {
          "value": "A/B Split",
          "depth": 1,
          "id": "ab-split"
        }, {
          "value": "Pausing and stopping a journey",
          "depth": 0,
          "id": "pausing-and-stopping-a-journey"
        }, {
          "value": "Resetting a journey",
          "depth": 0,
          "id": "resetting-a-journey"
        }, {
          "value": "Editing a journey",
          "depth": 0,
          "id": "editing-a-journey"
        }, {
          "value": "Journey Performance",
          "depth": 0,
          "id": "journey-performance"
        }, {
          "value": "Journey Logs",
          "depth": 0,
          "id": "journey-logs"
        }, {
          "value": "Journey Log Table Schema",
          "depth": 1,
          "id": "journey-log-table-schema"
        }, {
          "value": "Journey Metadata Table Schema",
          "depth": 1,
          "id": "journey-metadata-table-schema"
        }, {
          "value": "Global Journey View Schema",
          "depth": 1,
          "id": "global-journey-view-schema"
        }, {
          "value": "Global Journey Metadata View Schema",
          "depth": 1,
          "id": "global-journey-metadata-view-schema"
        }, {
          "value": "Best practices and FAQ",
          "depth": 0,
          "id": "best-practices-and-faq"
        }, {
          "value": "Journeys and priority lists",
          "depth": 1,
          "id": "journeys-and-priority-lists"
        }, {
          "value": "Failed syncs and retries",
          "depth": 1,
          "id": "failed-syncs-and-retries"
        }, {
          "value": "Sync sequencing",
          "depth": 1,
          "id": "sync-sequencing"
        }, {
          "value": "Journey sync configurations",
          "depth": 1,
          "id": "journey-sync-configurations"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      img: "img",
      a: "a",
      h3: "h3",
      strong: "strong",
      ul: "ul",
      li: "li",
      code: "code",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components), {IntegrationsCount} = _components;
    if (!IntegrationsCount) _missingMdxReference("IntegrationsCount", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch Journeys allows you to create automated sequences of interactions and data syncs. With journeys, you can trigger campaigns, wait for certain actions or events, personalize experiences based on member attributes, and sync data to hundreds of destinations."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-intro.png",
          alt: "Hightouch Journey"
        })
      }), "\n", _jsx(_components.h2, {
        id: "required-setup",
        children: "Required setup"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch Journeys are built on top of the same underlying ", _jsx(_components.a, {
          href: "/customer-studio/schema",
          children: "data schema"
        }), " that audiences use. Additionally, the parent model used in a journey needs to be using the ", _jsx(_components.a, {
          href: "/syncs/lightning-sync-engine",
          children: "lightning sync engine"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Journeys also leverage ", _jsx(_components.a, {
          href: "/syncs/overview",
          children: "syncs"
        }), " when you want to take some action inside of a journey such as triggering an email or sending a user into an ad campaign. You’ll want to ensure all the ", _jsx(_components.a, {
          href: "/getting-started/create-your-first-sync#connect-a-destination",
          children: "destinations"
        }), " you want to send users to are set up as well."]
      }), "\n", _jsx(_components.h2, {
        id: "build-a-journey",
        children: "Build a journey"
      }), "\n", _jsx(_components.h3, {
        id: "create-new-journey",
        children: "Create new journey"
      }), "\n", _jsxs(_components.p, {
        children: ["To create a new journey, go to the journeys overview page and click ", _jsx(_components.strong, {
          children: "add journey"
        }), " in the top right corner. You’ll be prompted to select a parent model (if you’ve set up more than one) and to give your journey a name and description. Finally, click ", _jsx(_components.strong, {
          children: "create journey"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You can also copy an existing journey rather than creating it from scratch. To make a copy,"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Make a copy"
          }), " from the Journey list page"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Make a copy"
          }), " from the horizontal three-dots menu in the Journey detail page ", _jsx(_components.img, {
            src: "hightouch-audiences/journeys/journey-make-a-copy.png",
            alt: "Copy a Journey"
          }), "\nUpdate the journey name and description as needed, and select ", _jsx(_components.strong, {
            children: "Create journey"
          }), " to save the new journey."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Copied Journeys share a Parent Model with the original Journey."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Copied journeys are automatically saved in a ", _jsx(_components.code, {
          children: "draft"
        }), " state. Make sure to review all journey details, particularly sync configurations, and make any required changes before enabling your copied journey."]
      }), "\n", _jsx(_components.h3, {
        id: "edit-view",
        children: "Edit view"
      }), "\n", _jsxs(_components.p, {
        children: ["After creating a new journey, you’ll land in the edit view. This is where you can add more steps or “tiles” to the journey and configure the logic for each one. At any point, you can click ", _jsx(_components.strong, {
          children: "save changes"
        }), " or ", _jsx(_components.strong, {
          children: "discard changes"
        }), " to create the associated action and exit the edit view. If you navigate away from this edit view with unsaved changes, those changes will be lost."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-edit-view.png",
          alt: "Journey edit view in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "interacting-with-the-canvas",
        children: "Interacting with the canvas"
      }), "\n", _jsx(_components.p, {
        children: "While in the edit view, you can build out your journey by dragging tiles onto the canvas from the panel on the right. Once you’ve created a new tile, you can click on it to view and edit its configuration."
      }), "\n", _jsx(_components.p, {
        children: "Some helpful tips for interacting with the canvas in edit view:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Pan"
          }), " by clicking on empty space and dragging or scrolling on a trackpad"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Zoom"
          }), " by using the +/- buttons in the top left, or pinching on a trackpad"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create a tile"
          }), " by dragging one onto the canvas from the library on the right"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Move a tile"
          }), " by clicking and dragging it around the canvas"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Edit a tile’s configuration"
          }), " by clicking on the tile"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Delete a tile"
          }), " by hovering over it and clicking the trash icon (note: the start tile cannot be deleted)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Duplicate a tile"
          }), " by hovering over it and clicking the copy icon (note: the start tile cannot be duplicated)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Clean up"
          }), " a messy canvas by clicking the clean up icon on the top left of the canvas"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Select multiple tiles"
          }), " by holding ", _jsx(_components.strong, {
            children: "Shift"
          }), " on your keyboard while clicking and dragging around a set of tiles. Once they're selected, you can then move all of these tiles as a group, copy them, or delete them."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "connecting-tiles",
        children: "Connecting tiles"
      }), "\n", _jsx(_components.p, {
        children: "Members will flow through your journey according to the connections you create between tiles. To connect two tiles, click and drag starting from the circular connector on the bottom of a tile. Drag the connector on to the target tile (you should see a green outline appear) and release."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-connect-tiles.png",
          alt: "Connecting tiles in a Journey in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "For some tiles such as “hold until” and “segment”, connections are created from smaller tiles underneath, rather than from the tile itself. You can see in the “hold until” example below how connections are created from the “rule met” and “rule not met” tiles, rather than from the yellow “hold until” tile."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-mini-tiles.png",
          alt: "Connecting mini tiles in a Journey in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Each connector dot can only have one connection coming out of it. This is to ensure journey members aren’t duplicated down multiple paths. However, you can have multiple connectors lead into the same tile, often referred to as a “merge” between journey paths."
      }), "\n", _jsx(_components.p, {
        children: "To delete a connection, hover over the middle of a connector and you’ll see a trash can icon appear."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-delete-connection.png",
          alt: "Deleting connections in a Journey in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "start-tile",
        children: "Start tile"
      }), "\n", _jsx(_components.p, {
        children: "The first tile in every journey is the start tile. The configuration of the start tile dictates who enters the journey, when, and how often."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey_start.png",
          alt: "Journey Start Tile"
        })
      }), "\n", _jsx(_components.p, {
        children: "Users may enter a journey in one of two ways:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["User enters an ", _jsx(_components.a, {
            href: "/customer-studio/journeys#audience-based-entry",
            children: "audience"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["User performs a specific ", _jsx(_components.a, {
            href: "/customer-studio/journeys#event-based-entry",
            children: "event"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Note that once the journey has run, you cannot change the entry method."
      }), "\n", _jsx(_components.h3, {
        id: "evaluation-schedule",
        children: "Evaluation schedule"
      }), "\n", _jsx(_components.p, {
        children: "Journeys are evaluated on a fixed schedule. The schedule defaults to every hour, and must be greater than every 5 minutes. A journey evaluation, or journey “run”, is when Hightouch looks at the current state of the journey, pushes records through the journey until they reach a time delay, and triggers all the syncs in the journey."
      }), "\n", _jsxs(_components.p, {
        children: ["To edit the schedule, ensure you’re in edit mode, locate the settings panel on the right hand side, and click ", _jsx(_components.strong, {
          children: "Edit."
        }), " You’ll see the settings drawer appear where you can choose between a regular interval or custom recurrence schedule type."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-schedule-card.png",
          alt: "Viewing the schedule in a Journey in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "In most cases, a 1 hour interval should be sufficient. Keep in mind that the journey schedule also dictates the sync schedule, so if you want all the syncs to be triggered at a specific time of day or interval, you’ll want to set the journey schedule accordingly."
      }), "\n", _jsx(_components.h3, {
        id: "starting-a-journey",
        children: "Starting a journey"
      }), "\n", _jsxs(_components.p, {
        children: ["To start a journey, ensure all your changes are saved, and then press ", _jsx(_components.strong, {
          children: "Start"
        }), " in the upper right. After pressing start, your journey is now live and you should see member counts appear inside each tile. If you used an interval schedule, the first run should kick off after a few seconds (you can refresh the page to check the updated counts). If you used a custom recurrence schedule, the first run will kick off at whatever time you specified."]
      }), "\n", _jsx(_components.p, {
        children: "Once the journey is live, you can pause or turn off the journey at any time by clicking the corresponding buttons in the upper right corner."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-stop-button.png",
          alt: "Viewing a live Journey in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "flexibility",
        children: "Flexibility"
      }), "\n", _jsxs(_components.p, {
        children: ["Just like Hightouch’s audience builder, the journey builder is super flexible and data-agnostic. You don’t have to necessarily run users through a journey. For b2b use cases, you can build a journey for accounts, opportunities, or workspaces. For marketplaces, you can build journeys for both merchants and consumers. For travel companies, you can build journeys based on trips or bookings. To accomplish these, you can build ", _jsx(_components.a, {
          href: "/customer-studio/schema#parent-models",
          children: "parent models"
        }), " in the schema builder that represent something other than users, and select that parent model when creating a new journey."]
      }), "\n", _jsx(_components.h2, {
        id: "entry-and-exit-criteria",
        children: "Entry and Exit criteria"
      }), "\n", _jsx(_components.h3, {
        id: "audience-based-entry",
        children: "Audience-based entry"
      }), "\n", _jsxs(_components.p, {
        children: ["When the journey begins running, all members of the entry ", _jsx(_components.a, {
          href: "/customer-studio/usage#overview",
          children: "audience"
        }), " will enter the journey. On subsequent journey runs, any new members that have entered the audience will automatically enter the journey."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "If a journey member drops out of the entry audience, they will remain in the journey. If a journey member drops out of the entire parent model, Hightouch no longer has access to that data and therefore they will exit the journey."
        })
      }), "\n", _jsx(_components.p, {
        children: "In the start tile, specify whether members may re-enter the journey. If the journey allows unlimited re-entries, then members are eligible to re-enter the journey after they finish. With unlimited entries, a member will re-enter if they finish the journey, drop out of the entry audience, and then re-enter the entry audience. As long as a member remains in the entry audience, they will not re-enter the journey."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/entry_audience_based.png",
          alt: "Viewing audience-based entry configuration"
        })
      }), "\n", _jsx(_components.h3, {
        id: "event-based-entry",
        children: "Event-based entry"
      }), "\n", _jsx(_components.p, {
        children: "Start by selecting the event that will be used as the entry criteria. Add additional properties as needed to further refine which events will trigger your journey."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Only events with a primary key defined can be used in journey entry criteria. Primary keys should be unique. In cases where they are not unique, only the first event - based on the event's timestamp - with that primary key will enter the journey."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When you start your journey, only members who perform the event ", _jsx(_components.em, {
          children: "after"
        }), " the start will enter the journey (i.e. events whose timestamps are later than the journey start time will enter the journey)."]
      }), "\n", _jsx(_components.p, {
        children: "In the start tile, specify whether members may re-enter the journey. If you allow unlimited entries, then you must also determine whether the same member can be in the journey multiple times at once. If only a single entry at a time is allowed, only the first event will trigger a journey trip for that member."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/entry_event_based.png",
          alt: "Viewing event-based entry configuration"
        })
      }), "\n", _jsx(_components.p, {
        children: "When you select event-based entry for your journey, that event’s properties can be referenced in other tiles in the journey and can be used in syncs triggered by the journey."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Reference an entry event property in a ", _jsx(_components.a, {
            href: "/customer-studio/journeys#send-to-destination",
            children: "sync"
          }), ". For example, you can send the category of the product that a user searched to your destination, so that you can reference the category name in emails. ", _jsx(_components.img, {
            src: "hightouch-audiences/journeys/sync_mapper_reference_entry.png",
            alt: "Viewing a reference to the entry event in a sync configuration"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Reference an entry event property in a ", _jsx(_components.a, {
            href: "/customer-studio/journeys#segment",
            children: "segment tile"
          }), ". For example, if users enter your journey after making a purchase, you can send them down different branches depending on the category of the product they purchased. ", _jsx(_components.img, {
            src: "hightouch-audiences/journeys/segment_reference_entry_event.png",
            alt: "Viewing a reference to the entry event in a segment tile"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Compare event properties to entry event properties in a ", _jsx(_components.a, {
            href: "/customer-studio/journeys#segment",
            children: "segment tile"
          }), ". For example, if users enter your journey after adding items to their carts, you can send them down different branches depending on whether they've added to cart or purchased other products with the same category in the past. ", _jsx(_components.img, {
            src: "hightouch-audiences/journeys/segment_compare_entry_event.png",
            alt: "Viewing a comparison of the entry event to other event properties in a segment tile"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Compare event properties to entry event properties in a ", _jsx(_components.a, {
            href: "/customer-studio/journeys#hold-until",
            children: "hold until tile"
          }), ". For example, you can hold a user in a this node until they purchase a product in the category that matches the category of a search that triggered their entry into a journey. ", _jsx(_components.img, {
            src: "hightouch-audiences/journeys/hold_compare_entry_event.png",
            alt: "Viewing a comparison of the entry event to other event properties in a hold until tile"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "exit-criteria",
        children: "Exit criteria"
      }), "\n", _jsx(_components.p, {
        children: "Exit criteria allow you to define global criteria to identify users who should immediately exit your journey regardless of which step they’re currently on. Use exit criteria to remove users from journeys when they take a key action (e.g. make a purchase) or their attributes change (e.g. opt out)."
      }), "\n", _jsx(_components.h2, {
        id: "tile-types",
        children: "Tile types"
      }), "\n", _jsx(_components.h3, {
        id: "send-to-destination",
        children: "Send to destination"
      }), "\n", _jsxs(_components.p, {
        children: ["This tile encompasses all the “actions” that you might want to take from within a Hightouch Journey such as triggering a campaign, sending users to ad platforms, updating records in a database, or sending records to any of our ", _jsx(IntegrationsCount, {}), " ", _jsx(_components.a, {
          href: "https://hightouch.com/integrations",
          children: "destination integrations"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["After dragging a “send to destination” tile onto the canvas, you can click the tile to open the configuration drawer, and click ", _jsx(_components.strong, {
          children: "Add sync"
        }), " to add a new sync. You’ll be prompted to save your work before jumping into the ", _jsx(_components.a, {
          href: "/syncs/overview#configuration",
          children: "sync configuration process"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You’ll notice one additional piece of sync configuration that is specific to journey syncs: specifying whether your sync is a “trigger” or “cohort” sync. The basic difference is:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Trigger"
          }), " syncs allow for the same parent model record (same user) to be synced multiple times without ever worrying about “removing” a record from the sync. This is useful if you have a sync that’s triggering a campaign in your email tool and you’ve enabled “unlimited entries” in the start tile. Thus when a record re-enters the journey, Hightouch will be able to trigger the campaign again."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Cohort"
          }), " syncs will ensure records are never duplicated in the downstream destination. These should be used when syncing into a segment/cohort object and you care about maintaining the right group of records in the destination. When choosing this option, you’ll additionally need to specify when, if ever, Hightouch should remove records from the destination cohort."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Review our ", _jsx(_components.a, {
          href: "#journey-sync-configurations",
          children: "best practices"
        }), " for selecting sync configurations."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-sync-config.png",
          alt: "Viewing a sync's configuration in a Journey in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "time-delay",
        children: "Time delay"
      }), "\n", _jsx(_components.p, {
        children: "This tile is used to hold records for a specified period of time in minutes, hours, days, weeks, months, or years. Because journeys run on a schedule, the time delay should be greater than or equal to your journey schedule to ensure that members progress through the journey as expected."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-time-delay.png",
          alt: "Viewing a time delay tile's configuration in a Journey in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "hold-until",
        children: "Hold until"
      }), "\n", _jsx(_components.p, {
        children: "This tile is used to hold records until they perform a specific action or event. You also need to specify a maximum hold duration after which, if the event hasn’t been performed, the record goes down the “rule not met” branch. This tile is useful if you want to take some action immediately following the event/action you’re waiting for."
      }), "\n", _jsx(_components.p, {
        children: "As you can see in the image below, you can further filter down the event of interest if you’re waiting for an event with specific properties."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-hold-until.png",
          alt: "Viewing a hold until tile's configuration in a Journey in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "segment",
        children: "Segment"
      }), "\n", _jsx(_components.p, {
        children: "This tile is used to split journey members based on any data you have available. Mutual exclusivity is automatically enforced between the segments you create here, meaning that a record can only ever fall into one segment. A catch-all “everyone else” segment is also created automatically so that you can specify what to do with members who don’t qualify for any of the created segments."
      }), "\n", _jsxs(_components.p, {
        children: ["To create a new segment, click ", _jsx(_components.strong, {
          children: "Add segment"
        }), " in the top right of the tile’s configuration drawer. Once created, you can specify which members should fall into this segment using a similar interface as the ", _jsx(_components.a, {
          href: "/customer-studio/usage#filter-conditions",
          children: "audience builder"
        }), ". To change the name of the segment, click one of the pencil icons at the top of the configuration drawer. At the bottom of the drawer you have the option to save changes, discard changes, or delete the segment."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-segment.png",
          alt: "Viewing a segment tile's configuration in a Journey in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Once you’ve created segments, they can be reprioritized by using the arrows on the left, or dragging/dropping using the handle. To jump back into a segment and edit it, you can click Edit or click on the segment name on the canvas."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-priority-list.png",
          alt: "Viewing a priority list tile's configuration in a Journey in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "ab-split",
        children: "A/B Split"
      }), "\n", _jsx(_components.p, {
        children: "This tile enables you to randomly assign members to different split groups within your journey, unlocking the ability to run A/B or multivariate testing. You can create as many split groups as needed (with a minimum of two), each with its own weight and name. For even more advanced testing, you can chain multiple A/B Split tiles together to set up multivariate experiments. Consistency is automatically maintained, ensuring that each record is always assigned to the same group on subsequent runs."
      }), "\n", _jsx(_components.p, {
        children: "To create a new split, click Add A/B Split in the top right of the tile’s configuration drawer."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-splits.png",
          alt: "Viewing a split tile's configuration in a Journey in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Once created, you can specify split percentages (weights), edit group names, or add/remove split groups. The sum of all weights must be 100 and group names must be unique within the split."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-splits-config.png",
          alt: "Viewing a priority list tile's configuration in a Journey in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "pausing-and-stopping-a-journey",
        children: "Pausing and stopping a journey"
      }), "\n", _jsx(_components.p, {
        children: "Once a journey is live you have the option to pause it or turn it off, both of which can be found in the upper right corner."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-pause-stop.png",
          alt: "Viewing a live journey in the Hightouch UI. The top bar, in the live state, contain buttons titled Turn off and Pause"
        })
      }), "\n", _jsx(_components.p, {
        children: "When turning off a journey, you’ll be presented with two options:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Allow existing members to complete the journey while closing it to new entries"
        }), "\n", _jsx(_components.li, {
          children: "Hard stop and remove all members immediately (note: hard stopping a journey will take a few moments to complete and the journey may briefly appear as “paused”)"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "resetting-a-journey",
        children: "Resetting a journey"
      }), "\n", _jsx(_components.p, {
        children: "When a journey is stopped, you have the option to reset it."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-reset.png",
          alt: "Viewing a stopped journey in the Hightouch UI. In the top bar, under a three-dot menu, is a button titled Reset journey"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Resetting a journey will clear out ", _jsx(_components.em, {
          children: "all"
        }), " internal state, making it behave as if you've never run the journey before. All entry history will be cleared and all syncs will be reset."]
      }), "\n", _jsx(_components.h2, {
        id: "editing-a-journey",
        children: "Editing a journey"
      }), "\n", _jsx(_components.p, {
        children: "Journeys can only be edited when in the “Paused”, “Draft”, or “Off” state. If you’d like to edit a live journey, you must first pause it."
      }), "\n", _jsx(_components.p, {
        children: "When editing a paused journey that has members inside of it, members will remain in their current tile and proceed in the new journey once resumed. If you delete a tile with members in it, those members will be removed from the journey."
      }), "\n", _jsx(_components.h2, {
        id: "journey-performance",
        children: "Journey Performance"
      }), "\n", _jsx(_components.p, {
        children: "Once you’ve launched your journey, the performance tile allows you to monitor the progress of your journey."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-performance-tile.png",
          alt: "Viewing the journey performance tile in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "The Entire journey section contains overall user counts for your journey as well as counts of unique users. These counts correspond to the entire history of the journey, unless you reset the journey in which case the counts reset to zero."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "In progress"
          }), ": users are currently in one of the journey tiles."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Entered"
          }), ": users who have ever entered this journey."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Met exit criteria"
          }), ": users who qualified out of the journey, at any step, due to your exit criteria."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Finished"
          }), ": users who completed the journey in full."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Select a tile to view counts for a specific node."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Currently in tile"
          }), ": users currently in this tile. Note: in practice, this count is typically 0 except for time delay tiles and hold until tiles."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Entered tile"
          }), ": users who have ever entered this tile."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Met exit criteria"
          }), ": users who exited this tile (and journey) after meeting global journey exit criteria."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Advanced from tile"
          }), ": users who exited this tile and proceeded to the following tile. For segment, A/B split, and hold until tiles, the aggregate count is available, as well as a breakdown of counts by each branch of the node. Toggle to see segment percentages."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Click any count to view the users in more detail. The drawer shows the 100 random profiles of users who meet the filter criteria. Click a specific user to view all attributes."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-performance-member-detail.png",
          alt: "Viewing the member details from journey performance tile in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "journey-logs",
        children: "Journey Logs"
      }), "\n", _jsx(_components.p, {
        children: "The Journeys system creates tables in your data warehouse to enable journey observation, reporting, and debugging."
      }), "\n", _jsxs(_components.p, {
        children: ["Each journey has two individual tables created in the ", _jsx(_components.code, {
          children: "HIGHTOUCH_PLANNER"
        }), " schema, where ", _jsx(_components.code, {
          children: "journey_id"
        }), " in the naming scheme is the UUID of the journey with the dashes removed."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "JOURNEY_LOG_<journey_id>"
          }), ": this table logs the progress of all rows through a given journey."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["This table is actually used as part of Journey execution, so while it’s safe\nto read from it to do analysis, ", _jsx(_components.em, {
            children: _jsx(_components.strong, {
              children: "please do not insert, update, or delete\nto/from it"
            })
          }), ", since this might cause the Journey to behave in unexpected\nways."]
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "JOURNEY_METADATA_<journey_id>"
          }), ": this table can be used to rebuild the journey graph and to access your node names for easier reporting."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Additionally, two global views are available in ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), " that contain a comprehensive view of all of a workspace’s journeys in the warehouse, giving customers a single read-only point to monitor their journeys. These tables have the same schema as their underlying tables in addition to a reference to the source table that the row came from. We recommend that you use these global views rather than the individual ", _jsx(_components.code, {
          children: "journey_log"
        }), " and ", _jsx(_components.code, {
          children: "journey_metadata"
        }), " tables for your warehouse reporting."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "JOURNEY_LOG_VIEW_<workspace_slug>"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "JOURNEY_METADATA_VIEW_<workspace_slug>"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "journey-log-table-schema",
        children: "Journey Log Table Schema"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "JOURNEY_LOG_<journey_id>"
        }), " logs the progress of all rows through a given journey."]
      }), "\n", _jsxs(_components.p, {
        children: ["Each time a row enters the journey, moves from one node to another, or exits the journey, we create an entry in the log table. Note that since a row can enter a journey more than once, we also have a ", _jsx(_components.code, {
          children: "ROW_INSTANCE_ID"
        }), " column on the table, which is used to uniquely identify each time a row enters the journey. That instance ID will be consistent for that particular instance of the row as it moves through the journey."]
      }), "\n", _jsx(Info, {
        children: "Note that resetting a journey will clear the log table."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "row_id"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The primary key from the journey’s parent model that this row represents."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "row_instance_id"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "A UUID to uniquely identify a row_id each time it enters the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "run_id"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the journey run that executed this operation. This is an internal detail."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "from_node_id"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the node that this action originates with. For moves or exits, it’s the node that the row moved or exited from. For enters, it’s NULL."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "to_node_id"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the node that this action targets. For moves and enters, it’s the node the row moved into. For exits, it’s NULL."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "The effective timestamp of this operation. Note that this does not always represent the actual time the operation occurred - for example, if a user moved from one node to another due to an event, this will be the timestamp of the event rather than the timestamp we actually ran the warehouse query."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "event_type"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["What type of event this log line represents. One of ", _jsx(_components.code, {
                children: "moved-to-node"
              }), ", ", _jsx(_components.code, {
                children: "entered-journey"
              }), ", ", _jsx(_components.code, {
                children: "exited-journey-by-criteria"
              }), ", or ", _jsx(_components.code, {
                children: "exited-journey"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "journey-metadata-table-schema",
        children: "Journey Metadata Table Schema"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "JOURNEY_METADATA_<journey_id>"
        }), " contains the positions of the nodes in a journey, and the customer-defined names of nodes."]
      }), "\n", _jsx(_components.p, {
        children: "Each time a change is saved to the Journey this table is updated."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "journey_id"
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The id of the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "journey_name"
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The customer-defined name of the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "node_name"
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The customer-defined name of the node."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "node_id"
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The ID of the journey node."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "to_nodes"
            }), _jsx(_components.td, {
              children: "text[]"
            }), _jsx(_components.td, {
              children: "An array of the outbound node ids from the given node."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "global-journey-view-schema",
        children: "Global Journey View Schema"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "JOURNEY_LOG_VIEW_<workspace_slug>"
        }), " view shows the progress of ", _jsx(_components.em, {
          children: "all"
        }), " rows in ", _jsx(_components.em, {
          children: "all"
        }), " journeys."]
      }), "\n", _jsx(_components.p, {
        children: "Each time the journey is added or removed from your workspace, this view is updated."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "source_table"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The name of the table this row came from: ", _jsx(_components.code, {
                children: "journey_log_<journey_id>"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "row_id"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The primary key from the journey’s parent model that this row represents."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "row_instance_id"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "A UUID to uniquely identify a row_id each time it enters the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "run_id"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the journey run that executed this operation. This is an internal detail."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "from_node_id"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the node that this action originates with. For moves or exits, it’s the node that the row moved or exited from. For enters, it’s NULL."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "to_node_id"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the node that this action targets. For moves and enters, it’s the node the row moved into. For exits, it’s NULL."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "The effective timestamp of this operation. Note that this does not always represent the actual time the operation occurred - for example, if a user moved from one node to another due to an event, this will be the timestamp of the event rather than the timestamp we actually ran the warehouse query."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "event_type"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["What type of event this log line represents. One of ", _jsx(_components.code, {
                children: "moved-to-node"
              }), ", ", _jsx(_components.code, {
                children: "entered-journey"
              }), ", ", _jsx(_components.code, {
                children: "exited-journey-by-criteria"
              }), ", or ", _jsx(_components.code, {
                children: "exited-journey"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "journey_id"
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The id of the journey."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "global-journey-metadata-view-schema",
        children: "Global Journey Metadata View Schema"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "JOURNEY_METADATA_VIEW_<workspace_slug>"
        }), " view contains the positions of the nodes across all journeys in the workspace and the customer-defined names of those nodes."]
      }), "\n", _jsx(_components.p, {
        children: "Each time the journey runs, this view is updated."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "source_table"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The name of the table this row came from: ", _jsx(_components.code, {
                children: "journey_log_<journey_id>"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "journey_id"
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The id of the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "journey_name"
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The customer-defined name of the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "node_name"
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The customer-defined name of the node."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "node_id"
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The ID of the journey node."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "to_nodes"
            }), _jsx(_components.td, {
              children: "text[]"
            }), _jsx(_components.td, {
              children: "An array of the outbound node ids from the given node."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "best-practices-and-faq",
        children: "Best practices and FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "journeys-and-priority-lists",
        children: "Journeys and priority lists"
      }), "\n", _jsx(_components.p, {
        children: "If your entry audience is inside of a priority list, the priority will be respected when determining who should enter the journey. For example, if your entry audience, Audience B, is prioritized underneath Audience A, and user 1 meets the criteria for both audiences, the user will only fall into Audience A. This means they will not enter the journey until they drop out of Audience A (while remaining in Audience B)."
      }), "\n", _jsx(_components.h3, {
        id: "failed-syncs-and-retries",
        children: "Failed syncs and retries"
      }), "\n", _jsx(_components.p, {
        children: "If a record fails to get synced inside of a journey, Hightouch will attempt to retry that record upon subsequent runs."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "For cohort syncs, Hightouch will stop retrying when the record is removed from the sync"
        }), "\n", _jsx(_components.li, {
          children: "For trigger syncs, Hightouch will continue to retry the record until it’s synced successfully"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "sync-sequencing",
        children: "Sync sequencing"
      }), "\n", _jsx(_components.p, {
        children: "It’s worth noting that all syncs in a journey get triggered at the same time (at the end of each journey run). So if you have two back-to-back sync tiles without any time delay between them, a record could be synced to both destinations at the same time. If you want to ensure some sequencing, a time delay is needed."
      }), "\n", _jsx(_components.h3, {
        id: "journey-sync-configurations",
        children: "Journey sync configurations"
      }), "\n", _jsx(_components.p, {
        children: "Use these guidelines to choose the optimal sync settings for your journey."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Trigger Mode"
        }), ", like the name implies, is usually used for triggered sends such as email or SMS campaigns. Trigger mode typically works best with ", _jsx(_components.code, {
          children: "insert"
        }), " mode to ensure that members don't receive the same message multiple times. In trigger mode, members are never removed from destinations."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Mode"
            }), _jsx(_components.th, {
              children: "Trigger Mode Journeys"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.strong, {
                    children: "Recommended"
                  })
                }), _jsx("li", {
                  children: "Members who enter or re-enter a tile during a given journey run are synced to the destination"
                }), _jsx("li", {
                  children: "Changes to members who have previously synced to the destination are ignored"
                })]
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: [_jsx(_components.strong, {
                    children: "Not Recommended"
                  }), ": syncs are triggered for new and updated members, which can lead to duplicate triggered sends"]
                }), _jsx("li", {
                  children: "Members who enter the tile will be synced to the destination"
                }), _jsxs("li", {
                  children: ["Changes to any member ", _jsx(_components.em, {
                    children: "who has ever synced to the destination"
                  }), " are synced to the destination"]
                })]
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Update"
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: [_jsx(_components.strong, {
                    children: "Not Recommended"
                  }), ": syncs are triggered for updated members, which can lead to duplicate triggered sends"]
                }), _jsxs("li", {
                  children: ["Changes to any member ", _jsx(_components.em, {
                    children: "who has ever synced to the destination"
                  }), " are synced to the destination"]
                }), _jsx("li", {
                  children: "New members entering the node will only sync to the destination if the member already exists in the destination"
                })]
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Mirror"
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.strong, {
                    children: "Not Recommended"
                  })
                }), _jsx("li", {
                  children: "All members who have ever passed through the send to destination tile are synced every time the journey runs"
                })]
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Diff"
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.strong, {
                    children: "Not Recommended"
                  })
                }), _jsx("li", {
                  children: "One file is sent for each operation. Any member who entered the node, has changed, or removed is synced to the destination."
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cohort Mode"
        }), " is usually used for audiences, lists, or any behavior where members should only appear in the destination once. Cohort mode works best with ", _jsx(_components.code, {
          children: "upsert"
        }), " and ", _jsx(_components.code, {
          children: "mirror"
        }), " sync configurations. When configuring delete behavior, syncs should ", _jsx(_components.strong, {
          children: "not"
        }), " be set to ", _jsx(_components.code, {
          children: "do nothing"
        }), " for cohort mode syncs."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Mode"
            }), _jsx(_components.th, {
              children: "Cohort Mode Journeys"
            }), _jsx(_components.th, {
              children: "Cohort Removal Behavior"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.strong, {
                    children: "Recommended"
                  })
                }), _jsx("li", {
                  children: "New members entering the tile during a given journey run will sync to the destination"
                }), _jsx("li", {
                  children: "Changes to members already in the destination will sync"
                })]
              })
            }), _jsx(_components.td, {
              children: _jsx("ul", {
                children: _jsx("li", {
                  children: "Members will be removed from the destination based on the journey configuration"
                })
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Update"
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.strong, {
                    children: "Not Recommended"
                  })
                }), _jsx("li", {
                  children: "Changes to members already in the destination will sync"
                }), _jsx("li", {
                  children: "New members entering the node will only sync to the destination if the member already exists in the destination"
                })]
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: "Members will be removed from the destination based on the journey configuration"
                }), _jsxs("li", {
                  children: ["Usually ", _jsx(_components.code, {
                    children: "deleteMode"
                  }), " for ", _jsx(_components.code, {
                    children: "update"
                  }), " syncs is set to ", _jsx(_components.em, {
                    children: "clear"
                  }), ", meaning the member remains in the destination but with all attributes cleared."]
                })]
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: [_jsx(_components.strong, {
                    children: "Not Recommended"
                  }), " because removal configurations aren't respected"]
                }), _jsx("li", {
                  children: "New members entering the node are synced to the destination"
                }), _jsx("li", {
                  children: "Members who are changed or removed are ignored"
                })]
              })
            }), _jsx(_components.td, {
              children: _jsx("ul", {
                children: _jsx("li", {
                  children: "No removal will occur, regardless of Journey settings"
                })
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Mirror"
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: [_jsx(_components.strong, {
                    children: "Recommended"
                  }), " when the members in the destination should exactly match the members in the journey"]
                }), _jsx("li", {
                  children: "No diff is performed. All members who have ever passed through the send to destination tile and not met removal criteria are synced to the destination"
                })]
              })
            }), _jsx(_components.td, {
              children: _jsx("ul", {
                children: _jsx("li", {
                  children: "Members will be removed from the destination based on the journey configuration"
                })
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Diff"
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.strong, {
                    children: "Not Recommended"
                  })
                }), _jsx("li", {
                  children: "One file is sent for each operation. Any member who entered the node, has changed, or removed is synced to the destination. "
                })]
              })
            }), _jsx(_components.td, {
              children: _jsx("ul", {
                children: _jsx("li", {
                  children: "No removal will occur, regardless of Journey settings"
                })
              })
            })]
          })]
        })]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
